import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n } from '@kostbar/i18n'
import type {
  AdminCustomerDetail,
  CustomerStoreScope,
  CustomersListResponse,
} from '@/api/endpoints/customers'
import { customersApi } from '@/api/endpoints/customers'
import { ApiError } from '@/api/types'
import { getErrorMessage } from '@/shared/utils/error-codes'

interface UseCustomersParams {
  search?: string
  status?: 'active' | 'deactivated'
  page?: number
  pageSize?: number
  tenantId?: number | null
  scopedToRestaurantId?: number | null
  enabled?: boolean
}

export function useCustomers(params?: UseCustomersParams) {
  return useQuery<CustomersListResponse>({
    queryKey: ['customers', params],
    queryFn: () => customersApi.list(params),
    enabled: params?.enabled ?? true,
  })
}

export function useCustomer(id?: number | null, tenantId?: number | null) {
  return useQuery<AdminCustomerDetail>({
    queryKey: ['customers', 'detail', id, tenantId],
    queryFn: () => {
      if (!id) throw new Error('customer_id_required')
      return customersApi.get(id, tenantId)
    },
    enabled: Boolean(id && tenantId),
  })
}

function handleError(error: unknown) {
  if (error instanceof ApiError) {
    toast.error(getErrorMessage(error.status, error.code))
  } else {
    toast.error(i18n.t('customers:toast.actionFailed'))
  }
}

export function useDeactivateCustomer(tenantId?: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => customersApi.deactivate(id, tenantId),
    onSuccess: () => {
      toast.success(i18n.t('customers:toast.deactivated'))
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: handleError,
  })
}

export function useReactivateCustomer(tenantId?: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => customersApi.reactivate(id, tenantId),
    onSuccess: () => {
      toast.success(i18n.t('customers:toast.reactivated'))
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: handleError,
  })
}

export function useResendCustomerVerification(tenantId?: number | null) {
  return useMutation({
    mutationFn: (id: number) => customersApi.resendVerification(id, tenantId),
    onSuccess: () => {
      toast.success(i18n.t('customers:toast.verificationSent'))
    },
    onError: handleError,
  })
}

export function useCustomerStoreScopes(
  id?: number | null,
  tenantId?: number | null,
) {
  return useQuery<{ items: Array<CustomerStoreScope> }>({
    queryKey: ['customers', 'store-scopes', id, tenantId],
    queryFn: () => {
      if (!id) throw new Error('customer_id_required')
      return customersApi.listStoreScopes(id, tenantId)
    },
    enabled: Boolean(id && tenantId),
  })
}

/**
 * Grant and revoke share one mutation: the toggle knows which way it is going, and both calls
 * are idempotent server-side, so there is nothing to reconcile if the two disagree.
 *
 * Invalidates the customer list as well as the scope query — the roster filter makes list
 * membership depend on the grant.
 */
export function useSetCustomerStoreScope(
  customerId?: number | null,
  tenantId?: number | null,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      restaurantId,
      granted,
    }: {
      restaurantId: number
      granted: boolean
    }) => {
      if (!customerId) throw new Error('customer_id_required')
      return granted
        ? customersApi.grantStoreScope(customerId, restaurantId, tenantId)
        : customersApi.revokeStoreScope(customerId, restaurantId, tenantId)
    },
    onSuccess: (_data, variables) => {
      toast.success(
        i18n.t(
          variables.granted
            ? 'customers:toast.storeScopeGranted'
            : 'customers:toast.storeScopeRevoked',
        ),
      )
      queryClient.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: handleError,
  })
}
